import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import date from 'helpers/date';
import KpiCard from './components/KpiCard';
import EventosTab from './tabs/EventosTab';
import VendasTab from './tabs/VendasTab';
import IngressosTab from './tabs/IngressosTab';
import ProdutosTab from './tabs/ProdutosTab';
import CuponsTab from './tabs/CuponsTab';
import dashboardStatistics from 'api/requests/dashboardStatistics';
import Loader from 'components/_UI/Loader';
import { ChevronDown } from 'react-feather';

const tabKeys = {
    eventos: 'eventos',
    vendas: 'vendas',
    ingressos: 'ingressos',
    produtos: 'produtos',
    cupons: 'cupons',
};

const tabs = [
    { name: tabKeys.eventos, label: 'Eventos' },
    { name: tabKeys.vendas, label: 'Vendas' },
    { name: tabKeys.ingressos, label: 'Ingressos' },
    { name: tabKeys.produtos, label: 'Produtos' },
    { name: tabKeys.cupons, label: 'Cupons' },
];

const periodOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: '7d', label: '7 dias' },
    { value: '15d', label: '15 dias' },
    { value: '30d', label: '30 dias' },
    { value: 'custom', label: 'Personalizado' },
];

export default function Statistics() {
    const [selectedTab, setSelectedTab] = useState(tabKeys.eventos);
    const [selectedPeriod, setSelectedPeriod] = useState('30d');
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [customRange, setCustomRange] = useState(null);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const calendarRef = useRef(null);

    async function fetchStatistics(period, dateRange) {
        setIsLoading(true);
        try {
            const params = dateRange
                ? { start_date: dayjs(dateRange[0]).format('YYYY-MM-DD'), end_date: dayjs(dateRange[1]).format('YYYY-MM-DD') }
                : { period };

            const response = await dashboardStatistics.get(params);
            setData(response.data);
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (selectedPeriod === 'custom') {
            if (customRange) {
                fetchStatistics(null, customRange);
            }
            return;
        }
        setCustomRange(null);
        fetchStatistics(selectedPeriod);
    }, [selectedPeriod, customRange]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function handleCalendarChange(range) {
        if (!Array.isArray(range) || range.length < 2) return;

        const diffDays = dayjs(range[1]).diff(dayjs(range[0]), 'day');
        if (diffDays > 30) {
            alert('O intervalo máximo é de 30 dias.');
            return;
        }

        setCustomRange(range);
        setShowCalendar(false);
    }

    function getSelectedLabel() {
        if (selectedPeriod === 'custom' && customRange) {
            return `${date.format(customRange[0])} - ${date.format(customRange[1])}`;
        }
        return periodOptions.find(p => p.value === selectedPeriod)?.label;
    }

    if (isLoading) {
        return (
            <Container>
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            </Container>
        );
    }

    if (!data) {
        return (
            <Container>
                <EmptyState>Não foi possível carregar as estatísticas.</EmptyState>
            </Container>
        );
    }

    return (
        <>
            <KpiGrid>
                <KpiCard
                    label="Eventos Ativos"
                    value={data.kpis.eventosAtivos}
                    trend={data.kpis.eventosAtivosTrend}
                    gradient="linear-gradient(135deg, #8025e8 0%, #a855f7 100%)"
                />
                <KpiCard
                    label="Total de Cidades"
                    value={data.kpis.totalCidades}
                    trend={data.kpis.totalCidadesTrend}
                    gradient="linear-gradient(135deg, #3F6BC6 0%, #4F86F7 100%)"
                />
                <KpiCard
                    label="Receita Total"
                    value={currency.format(data.kpis.receitaTotal)}
                    trend={data.kpis.receitaTotalTrend}
                    gradient="linear-gradient(135deg, #2BC866 0%, #41D679 100%)"
                />
                <KpiCard
                    label="Participantes"
                    value={data.kpis.participantes >= 1000 ? `${(data.kpis.participantes / 1000).toFixed(1)}k` : data.kpis.participantes}
                    trend={data.kpis.participantesTrend}
                    gradient="linear-gradient(135deg, #E7505A 0%, #ff6b6b 100%)"
                />
            </KpiGrid>

            <Header>
                <HeaderRight>
                    <PeriodSelector onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}>
                        <span>{getSelectedLabel()}</span>
                        <ChevronDown size={16} color={colors.night} />
                        {showPeriodDropdown && (
                            <PeriodDropdown>
                                {periodOptions.map(option => (
                                    <PeriodOption
                                        key={option.value}
                                        selected={option.value === selectedPeriod}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (option.value === 'custom') {
                                                setSelectedPeriod('custom');
                                                setShowPeriodDropdown(false);
                                                setShowCalendar(true);
                                                return;
                                            }
                                            setSelectedPeriod(option.value);
                                            setShowPeriodDropdown(false);
                                        }}
                                    >
                                        {option.label}
                                    </PeriodOption>
                                ))}
                            </PeriodDropdown>
                        )}
                    </PeriodSelector>

                    {showCalendar && (
                        <CalendarPopover ref={calendarRef}>
                            <Calendar
                                selectRange={true}
                                value={customRange}
                                onChange={handleCalendarChange}
                                maxDate={new Date()}
                            />
                        </CalendarPopover>
                    )}
                </HeaderRight>
            </Header>
            
            <Container>

                <TabsContainer>
                    {tabs.map(tab => (
                        <TabItem
                            key={tab.name}
                            active={selectedTab === tab.name}
                            onClick={() => setSelectedTab(tab.name)}
                        >
                            {tab.label}
                        </TabItem>
                    ))}
                </TabsContainer>

                <TabContent>
                    {selectedTab === tabKeys.eventos && <EventosTab data={data.eventos} />}
                    {selectedTab === tabKeys.vendas && <VendasTab data={data.vendas} />}
                    {selectedTab === tabKeys.ingressos && <IngressosTab data={data.ingressos} />}
                    {selectedTab === tabKeys.produtos && <ProdutosTab data={data.produtos} />}
                    {selectedTab === tabKeys.cupons && <CuponsTab data={data.cupons} />}
                </TabContent>
            </Container>
        </>
    );
}

const Container = styled(Column)`
    width: 100%;
    padding: 0;
    background: #FAFBFD;
    min-height: 100vh;
`;

const Header = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 12px;
`;


const HeaderRight = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const PeriodSelector = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralLight};
    user-select: none;

    &:hover {
        border-color: ${colors.secondaryPurple};
    }
`;

const PeriodDropdown = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: ${colors.neutralLight};
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    min-width: 120px;
    overflow: hidden;
`;

const PeriodOption = styled.div`
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
    color: ${({ selected }) => selected ? colors.secondaryPurple : colors.nightDark};
    background: ${({ selected }) => selected ? '#F3E8FF' : 'transparent'};
    font-weight: ${({ selected }) => selected ? '600' : '400'};

    &:hover {
        background: #f5f5f5;
    }
`;

const CalendarPopover = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    background: ${colors.neutralLight};
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 20;
    padding: 8px;

    .react-calendar {
        border: none;
        font-family: inherit;
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        right: auto;
        left: 50%;
        transform: translateX(-50%);
    }
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralLight};
    transition: all 0.2s;

    &:hover {
        border-color: ${colors.secondaryPurple};
        color: ${colors.secondaryPurple};
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        span {
            display: none;
        }
        padding: 8px 10px;
    }
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 20px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const TabsContainer = styled(Row)`
    background: ${colors.neutralLight};
    border-radius: 8px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    margin-bottom: 20px;
    overflow-x: auto;
`;

const TabItem = styled.div`
    padding: 10px 20px;
    cursor: pointer;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: ${({ active }) => active ? colors.secondaryPurple : colors.night};
    background: ${({ active }) => active ? '#F3E8FF' : 'transparent'};
    border-radius: 6px;
    transition: all 0.2s ease;
    white-space: nowrap;
    flex: 1;

    &:hover {
        color: ${colors.secondaryPurple};
    }
`;

const TabContent = styled.div`
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
`;

const EmptyState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    font-size: 16px;
    color: ${colors.night};
`;
